import moment, { Moment } from 'moment-timezone';
import { Subject } from 'rxjs';
import { Gizmo } from '../../gizmos';
import { IGizmo, IGizmoData } from '../../types';
import CommonBinder from '../common-binder';

interface IDateFieldBinder {
  init(): Promise<void>;
}

class DateFieldBinder extends CommonBinder implements IDateFieldBinder {
  private _format: string = 'DD/MM/YYYY';
  /**
   * Observable which will be used by the UI layer in order to avoid Flowy
   * data format
   */
  public value: Subject<string>;

  constructor(gizmo: Gizmo) {
    super(gizmo);

    this.value = new Subject<string>();

    this._gizmo.data.subscribe(
      (data: IGizmoData | IGizmoData[] | undefined) => {
        if (
          data &&
          !Array.isArray(data) &&
          (typeof data.v === 'string' ||
            typeof data.v === 'number' ||
            data.v instanceof Date)
        ) {
          this.value.next(moment(data.v).format(this._format));
        } else if (data === undefined) {
          this.value.next('');
        }
      }
    );
  }

  public init = (): Promise<void> => {
    return new Promise<void>(async resolve => {
      const config: IGizmo = this._gizmo.getConfig();

      if (config.ops?.value?.flux && !this._gizmo.getData()) {
        const flux = this._gizmo.getFlux();

        if (flux) {
          let conditionResult = true;

          if (config.ops.value.condition) {
            const cr = await flux.evaluate({
              script: config.ops.value.condition,
            });

            if (cr !== undefined) {
              conditionResult = cr;
            }
          }

          if (conditionResult === true) {
            const result = await flux.evaluate({
              script: config.ops.value.flux,
            });

            if (result && typeof result === 'string') {
              await this.setValue(result);
            }
          }
        }
      } else if (
        config.ops?.dateField?.defaultValue === 'today' &&
        !this._gizmo.getData()
      ) {
        const today = moment();
        await this.setValue(today.toISOString());
      }

      resolve();
    });
  };

  /**
   * Get date format string
   * @returns
   */
  public getFormat = (): string => {
    return this._format;
  };

  /**
   * Function used to set an answer‰
   *
   * @param {string} value value being used by the UI layer
   *
   * @returns
   */
  public setValue = (value?: string): Promise<void> => {
    return new Promise<void>(async resolve => {
      let momentDate: Moment | undefined;

      if (value === undefined || value === '') {
        await this._gizmo.setData(undefined);
        return resolve();
      }

      const regex = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2}).(\d{3})Z$/;
      if (regex.test(value)) {
        // This will be true when loading data from the user extra data
        momentDate = moment(value);
      } else {
        momentDate = moment(value, this._format);
      }

      if (momentDate.isValid()) {
        await this._gizmo.setData({
          v: momentDate.toDate().toISOString(),
          dd: {
            timestamp: momentDate.toDate().getTime(),
            timezone: moment.tz.guess(),
          },
        });
      }

      resolve();
    });
  };

  /**
   * Get date value as a string
   *
   * @returns {string | undefined} date as a string
   */
  public getValue = (): string | undefined => {
    const data = this._gizmo.getData() as IGizmoData;

    if (
      data?.v &&
      (typeof data.v === 'string' ||
        typeof data.v === 'number' ||
        data.v instanceof Date)
    ) {
      const momentDate = moment(data.v);
      if (momentDate.isValid()) {
        return momentDate.format(this._format);
      }
    }

    return undefined;
  };
}

export default DateFieldBinder;
